import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";

/**
 * VaultLockGate — wraps the Statement Password Vault with an extra password gate.
 *
 * On mount:
 *   - GET /statement-passwords/lock/status to determine whether the vault is
 *     configured AND unlocked in this session
 *   - If configured=false → transparent pass-through (backend also stays open)
 *   - If configured=true && unlocked=true → render children
 *   - Else → render the unlock form
 *
 * The unlock cookie is HTTPOnly + short-lived (~30 min) — set by the backend
 * on POST /statement-passwords/lock/unlock. This component just presents the UI.
 */
export default function VaultLockGate({ children }) {
    const [status, setStatus] = useState(null);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () =>
        api.get("/statement-passwords/lock/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, unlocked: true }));

    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e?.preventDefault();
        if (!password) { toast.error("Enter the vault password"); return; }
        setBusy(true);
        try {
            await api.post("/statement-passwords/lock/unlock", { password });
            toast.success("Vault unlocked · 30-minute session");
            setPassword("");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Wrong vault password");
        } finally {
            setBusy(false);
        }
    };

    if (!status) {
        return <div className="p-10 flex items-center gap-2 text-stone-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading vault…</div>;
    }
    if (status.unlocked) return children;

    return (
        <div className="max-w-md mx-auto mt-10 p-6 rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="vault-lock-gate">
            <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center mb-3">
                    <Lock size={22} />
                </div>
                <div className="eyebrow">Extra security · Owner-only</div>
                <h1 className="text-2xl font-medium text-stone-900 mt-1">Vault is locked</h1>
                <p className="text-sm text-stone-500 mt-2">
                    The Statement Password Vault is protected by an additional password. Enter it to unlock this session for {status.ttl_minutes ?? 30} minutes.
                </p>
            </div>

            <form className="mt-6 space-y-3" onSubmit={submit}>
                <div>
                    <Label>Vault password</Label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter vault password"
                        autoFocus
                        className="mt-1"
                        data-testid="vault-lock-password"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={busy || !password}
                    className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                    data-testid="vault-lock-submit"
                >
                    {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Lock size={14} className="mr-1" />}
                    Unlock vault
                </Button>
            </form>

            <div className="flex items-start gap-2 text-[11px] text-stone-500 mt-5 pt-4 border-t border-stone-100">
                <ShieldCheck size={12} className="text-emerald-600 mt-0.5" />
                <span>Every unlock attempt is audit-logged. Session cookie is HTTPOnly and auto-expires.</span>
            </div>
        </div>
    );
}
